/**
 * Application Configuration
 * Centralized access to environment variables with defaults.
 *
 * In production (Docker) values are injected at container startup into
 * window.__env__ by docker-entrypoint.sh via a mounted .env file.
 * In local development Vite's normal import.meta.env fallback is used.
 *
 * IMPORTANT: config uses ES getters so each property is read from
 * window.__env__ at access time, not at module import time. This ensures
 * the runtime config.js script has been parsed before any value is consumed.
 */

import { getEnv } from './env'

export const config = {
    get serverName() {
        return getEnv('VITE_SERVER_NAME') || 'dev1'
    },

    // App Identity (white-label ready)
    get app() {
        return {
            name: getEnv('VITE_APP_NAME') || 'Agent MonDAI',
            tagline: getEnv('VITE_APP_TAGLINE') || 'Learn, Earn, and Connect',
            communityName: getEnv('VITE_COMMUNITY_NAME') || 'CryptoMondays',
        }
    },

    // Supabase Configuration
    get supabase() {
        return {
            url: getEnv('VITE_SUPABASE_URL'),
            anonKey: getEnv('VITE_SUPABASE_ANON_KEY'),
        }
    },

    // Removed KBAI config since it now runs server-side

    // LLM Configuration
    get llm() {
        return {
            // Proxy endpoint for community mode (local /ai endpoint)
            proxyUrl: getEnv('VITE_LLM_PROXY_URL') || '/ai',
            // Direct endpoints (for personal/local modes)
            communityProvider: getEnv('VITE_COMMUNITY_LLM_PROVIDER') || 'openai',
            communityModel: getEnv('VITE_COMMUNITY_LLM_MODEL') || 'gpt-5-mini',
            communityEndpoint: getEnv('VITE_COMMUNITY_LLM_ENDPOINT') || 'https://api.openai.com/v1',
            dailyTokenLimit: Number(getEnv('VITE_COMMUNITY_TOKEN_LIMIT_DAILY')) || 10000,
        }
    },

    // Data API Configuration (Luma, Member Import, etc.)
    get dataApi() {
        return {
            baseUrl: getEnv('VITE_DATA_API_BASE_URL') || '/api',
            // Shared token for Data API
            token: getEnv('VITE_DATA_API_TOKEN'),
        }
    },

    // Events Configuration
    get events() {
        const rawDefault = getEnv('VITE_EVENT_DEFAULT_TAB') || 'global';
        const defaultTab = rawDefault === 'upcoming' ? 'submitted' : rawDefault;
        return {
            defaultTab: defaultTab as 'submitted' | 'featured' | 'global' | 'past' | 'archive',
            lumaCalendarId: getEnv('VITE_EVENT_LUMA_CALENDAR_ID') || 'global',
        }
    },

    // Archive Configuration
    get archive() {
        return {
            transcriptCollection: getEnv('VITE_TRANSCRIPT_SEARCH_COLLECTION'),
        }
    },

    // Authentication Configuration
    get auth() {
        return {
            siteUrl: (getEnv('VITE_SITE_URL') || '').replace(/\/$/, ''),
            websiteInfoUrl: getEnv('VITE_WEBSITE_INFO_URL'),
        }
    },

    // Session Management
    get session() {
        return {
            /** How many minutes idle before triggering a session refresh (env: VITE_IDLE_THRESHOLD_MINUTES) */
            idleThresholdMinutes: Number(getEnv('VITE_IDLE_THRESHOLD_MINUTES')) || 10,
            /** Minutes idle before forcing a full page reload for clean state (env: VITE_FORCE_RELOAD_MINUTES) */
            forceReloadMinutes: Number(getEnv('VITE_FORCE_RELOAD_MINUTES')) || 20,
            /** Minutes before store data is considered stale and re-fetched on next view (env: VITE_DATA_STALE_MINUTES) */
            dataStaleMinutes: Number(getEnv('VITE_DATA_STALE_MINUTES')) || 5,
        }
    },

    // External Connections (Identity linkage for third-party platforms)
    get connections() {
        return {
            telegram: {
                enabled: getEnv('VITE_TELEGRAM_ENABLED') === 'true',
            },
            discord: {
                enabled: getEnv('VITE_DISCORD_ENABLED') === 'true',
            }
        }
    },

    get isDev() {
        return !!import.meta.env.DEV;
    },

    // Feature flags (static — not env-driven)
    features: {
        oauthGoogle: true,
        oauthGithub: false,
        oauthDiscord: false,
        communityLLM: true,
        personalLLM: true,
        localLLM: true,
        memberImport: true,
    },
}

export type AppConfig = typeof config
